import Phaser from 'phaser';
import i18next from 'i18next';
import { platform } from '@/platform';
import { isClipMode, isClipRules, isClipLessonSpent, boardsPerLesson } from '@/game/clip';
import { getActivePack, packKey } from '@/game/packs';
import { APP_STORE_URL } from '@/game/promo';
import { showPromoCard } from '@/game/ui/promoCard';
import { nextOutroVariant } from '@/game/clipOutroVariant';
import { createStoreBadges } from '@/game/ui/storeBadges';
import { createCardQr } from '@/game/ui/cardQr';
import { isDesktopBrowser } from '@/game/qrPromo';
import { trackTrialSpent } from '@/analytics';

/**
 * The end-of-trial card for the App Clip and the web demo: this lesson has played its allowance,
 * here is what the full app is, and here are the two ways forward.
 *
 * Written for the PARENT, not the child — Apple makes App Clips unavailable to accounts under 13
 * and to any account with Ask to Buy, so the person reading this is the buyer. Hence the two
 * claims that decide a purchase for a reading game: it builds real reading fluency, and the same
 * ten languages make it work for second-language learning too.
 *
 * The ghost action reopens the lesson picker rather than dismissing. Every lesson has its own allowance
 * (see src/game/clip.ts), so "try another lesson" is a real offer and it is what shows the parent
 * the breadth — the trial is meant to be browsed, not endured.
 *
 * Because the trial is meant to be browsed, this card is shown more than once — so it does not say
 * the same thing twice. The headline and body rotate through three cases (reading → price →
 * rewards) while everything that ACTS stays put; see src/game/clipOutroVariant.ts for the order and
 * why it is that order.
 */
export interface ClipOutroOptions {
  delayMs?: number;
  /**
   * Card size, `'lg'` unless told otherwise (see `?outro=md` in MenuScene). Settled by looking at
   * both on a phone, which is the only place that question can be answered.
   */
  size?: 'md' | 'lg';
  /** Runs when the player chooses "try another lesson". Omit to just dismiss the card. */
  onPickLesson?: () => void;
}

/**
 * Show the card if we are in the clip AND the active lesson is spent. Safe to call from any
 * end-of-game screen; every gate lives in here, exactly like maybeShowBuyPrompt.
 */
export function maybeShowClipOutro(scene: Phaser.Scene, opts: ClipOutroOptions = {}): void {
  if (!isClipMode()) return;
  if (!isClipLessonSpent(packKey(getActivePack()))) return;
  showClipOutro(scene, opts);
}

/**
 * The card itself, ungated — so the menu can show it the moment a spent lesson is chosen.
 *
 * The sale has two shapes:
 *
 *  - **App Clip** — one primary button driving Apple's own SKOverlay, which installs the paid app
 *    in place instead of throwing the player out to the App Store. It is the only sanctioned way
 *    for a clip to sell its full app, and a Google Play badge would be nonsense on an iPhone.
 *  - **Web demo** (`?demo`) — the official store badge row instead, because the browser player
 *    could be on either platform and we cannot know which. Same artwork the web buy card uses, so
 *    there is one "buy it" visual across the site.
 *
 * The branch is `isClipRules()`, NOT the presence of the bridge. Those look interchangeable —
 * only the clip has `requestUpsell` — but they part company under `?clip`, where the clip's flow
 * is reviewed in a browser that has no bridge at all. Keying on the bridge sent that review down
 * the web branch and showed an Android badge for an iPhone-only card, which is precisely the
 * thing the review exists to catch. The bridge still decides *how* the sale is made below.
 */
export function showClipOutro(scene: Phaser.Scene, opts: ClipOutroOptions = {}): void {
  const asClip = isClipRules();
  const upsell = platform().requestUpsell;
  trackTrialSpent(packKey(getActivePack()));   // web demo only; a no-op in the clip and everywhere else

  const actions = [];
  if (opts.onPickLesson) {
    actions.push({
      label: i18next.t('clip.another'),
      kind: 'ghost' as const,
      onClick: opts.onPickLesson,
    });
  }
  if (asClip) {
    actions.push({
      label: i18next.t('clip.get'),
      kind: 'primary' as const,
      // SKOverlay on a device; the plain App Store link is the fallback for `?clip` in a browser,
      // where there is no bridge to ask.
      onClick: () => {
        if (upsell) upsell();
        else window.open(APP_STORE_URL, '_blank', 'noopener');
      },
    });
  } else if (actions.length === 0) {
    // The badges are links, not card actions, so without this the web card would have no button
    // at all and nothing to dismiss it with.
    actions.push({ label: i18next.t('buy.later'), kind: 'ghost' as const });
  }

  // Which of the three cases this card makes — what the game is, what it costs, or what is still
  // waiting further up the ladder. It rotates per showing, because a parent browsing a second
  // lesson has already read the first pitch and a repeat of it adds nothing (clipOutroVariant.ts).
  // Only the words rotate: the sale itself, the badges and the way back are the same every time.
  const variant = nextOutroVariant();

  // The allowance line only means something when there IS an allowance. The demo's Saturn run has
  // no board cap (clip.ts demoRunsToSaturn), and this card cannot reach a player in that mode
  // today — a lesson with an infinite cap never spends — but the line would read "you have played
  // the Infinity free rounds of this lesson" the first time anything else opened the card, which is
  // the kind of string that ships. Dropped rather than papered over with a made-up number.
  const cap = boardsPerLesson();
  const lineKeys = Number.isFinite(cap)
    ? variant.lineKeys
    : variant.lineKeys.filter((key) => key !== 'clip.spent');

  // A QR instead of the badges, for the variant that asked for one — and only where anyone could
  // use it. `prefersQr` says the variant would rather have a code; `isDesktopBrowser()` says
  // whether it would be worth anything. On a phone it would not be: the device reading the screen
  // is the device that would install the app, so a code it cannot scan replaces a working ask with
  // a dead one (src/game/qrPromo.ts argues this at greater length). Phones therefore keep the
  // badges on every variant, and the credentials card differs from its three siblings only on a
  // laptop. Never true in the App Clip, which is an iPhone by definition and shows no `extra` at
  // all — hence `asClip` first, before the question is even asked.
  const withQr = !asClip && variant.prefersQr === true && isDesktopBrowser();

  showPromoCard(scene, {
    title: i18next.t(variant.titleKey),
    // `count` is only read by `clip.spent`; the other lines ignore it, so one call site serves
    // every variant and adding another needs no change here.
    lines: lineKeys.map((key) => i18next.t(key, { count: cap })),
    // Badge height is set here rather than in CSS (the artwork is measured, see storeBadges.ts),
    // so it has to be raised by hand to keep up with the larger type around it.
    extra: asClip ? undefined : (withQr ? createCardQr() : createStoreBadges({ height: 54 })),
    actions,
  }, {
    delayMs: opts.delayMs ?? 1200,
    size: opts.size ?? 'lg',
    // The code goes BESIDE the text, not under it. Stacked, this card measured 553–599 units
    // depending on language against a budget of 524, and the overflow lands on Replay — see
    // promoCard.placement.test.ts, which carries the measurements for both layouts.
    className: withQr ? 'promo-card--qr-side' : undefined,
  });
}
